/* SIZESOFTX
=========================================================================================
* Descripción: Página pública para revisar y aceptar el contrato de uso de una invitación.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      2        | 30/10/2026 | José Luis Aragón Cervantes       | Sanitización segura del contrato.
*      1        | 15/09/2026 | José Luis Aragón   Cervantes     | Creación de aceptación pública.
* ---------------------------------------------------------------------------------------
*/

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import SecurityIcon from "@mui/icons-material/Security";
import SendIcon from "@mui/icons-material/Send";
import { apiContratoAceptar, apiContratoPublico } from "../settings/supportApi";
import { sanitizarContratoHtml } from "../settings/contractHtml";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOKEN_REGEX = /^[A-Za-z0-9_-]{20,160}$/;

const limpiarEmail = (valor) => String(valor || "").trim().toLowerCase().slice(0, 180);

const formatoFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  return fecha.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
};

const ContratoPublicoPage = () => {
  const { token = "" } = useParams();
  const tokenValido = TOKEN_REGEX.test(token);
  const [contrato, setContrato] = useState(null);
  const [email, setEmail] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  const cargarContrato = useCallback(async () => {
    if (!tokenValido) {
      setError("La liga de contrato no es válida.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await apiContratoPublico(token);
      setContrato(data);
      setEmail(limpiarEmail(data?.correo));
    } catch (err) {
      setError(err?.message || "No se pudo cargar el contrato.");
    } finally {
      setLoading(false);
    }
  }, [token, tokenValido]);

  useEffect(() => {
    cargarContrato();
  }, [cargarContrato]);

  const htmlContrato = useMemo(() => sanitizarContratoHtml(contrato?.htmlContrato), [contrato]);
  const emailValido = EMAIL_REGEX.test(email);
  const puedeAceptar = Boolean(contrato?.valido) && emailValido && acepta && !contrato?.yaAceptado && !enviando;

  const aceptarContrato = async () => {
    if (!puedeAceptar) return;
    setEnviando(true);
    setError("");
    setResultado(null);
    try {
      const data = await apiContratoAceptar(token, { email: limpiarEmail(email) });
      setResultado(data);
      setContrato((prev) => ({
        ...(prev || {}),
        yaAceptado: Boolean(data?.aceptado),
        claveEntregadaPorCorreo: Boolean(data?.claveEnviadaEmail),
      }));
    } catch (err) {
      setError(err?.message || "No se pudo registrar la aceptación del contrato.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb", py: { xs: 2, md: 5 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Paper elevation={2} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h4" fontWeight={800}>Contrato de uso SizeSoftX</Typography>
                </Stack>
                <Typography color="text.secondary">
                  Revisa los términos de la invitación y confirma la aceptación con el correo al que se envió el contrato.
                </Typography>
              </Box>
              <Chip icon={<SecurityIcon />} label="Aceptación segura" color="success" variant="outlined" />
            </Stack>
          </Paper>

          {loading ? (
            <Paper sx={{ p: 5, textAlign: "center", borderRadius: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando contrato...</Typography>
            </Paper>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : contrato ? (
            <>
              <Paper elevation={1} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h5" fontWeight={800}>{contrato.titulo || "Contrato de uso"}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Empresa: ${contrato.empresa || contrato.razonSocial || "—"}`} />
                    <Chip label={`Licencia: ${contrato.licenseCode || "—"}`} color="primary" variant="outlined" />
                    <Chip label={`Versión: ${contrato.versionContrato || "—"}`} variant="outlined" />
                    <Chip label={`Vence: ${formatoFecha(contrato.expiraUtc)}`} color="warning" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Contacto: {contrato.usuario || "—"} · {contrato.correo || "—"}
                  </Typography>
                </Stack>
              </Paper>

              {contrato.yaAceptado && (
                <Alert icon={<CheckCircleOutlineIcon />} severity="success">
                  Este contrato ya fue aceptado. Si la clave ya fue enviada, el cliente debe revisar su correo y entrar a Tenant para activar la licencia.
                </Alert>
              )}

              {resultado && (
                <Alert icon={<CheckCircleOutlineIcon />} severity={resultado.claveEnviadaEmail ? "success" : "warning"}>
                  {resultado.mensaje || "Contrato aceptado."}
                </Alert>
              )}

              <Paper elevation={1} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
                <Box
                  sx={{
                    '& h1, & h2, & h3': { color: '#0b3558' },
                    '& p, & li': { fontSize: 16, lineHeight: 1.65 },
                    '& table': { width: '100%', borderCollapse: 'collapse' },
                    '& td, & th': { border: '1px solid #dde3ea', p: 1 },
                    '& a': { color: '#0b66b2' },
                  }}
                  dangerouslySetInnerHTML={{ __html: htmlContrato }}
                />
              </Paper>

              <Paper elevation={2} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                <Stack spacing={2.5}>
                  <Typography variant="h6" fontWeight={800}>Aceptar contrato</Typography>
                  <Alert severity="info">
                    Al aceptar, Support registra fecha, correo, IP y navegador como evidencia de aceptación. Después enviará por correo la clave de activación y la liga de Tenant.
                  </Alert>
                  <TextField
                    label="Correo de confirmación"
                    value={email}
                    onChange={(e) => setEmail(limpiarEmail(e.target.value))}
                    error={Boolean(email) && !emailValido}
                    helperText="Debe coincidir con el correo al que se envió la invitación."
                    inputProps={{ maxLength: 180 }}
                    disabled={contrato.yaAceptado}
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acepta || contrato.yaAceptado}
                        onChange={(e) => setAcepta(e.target.checked)}
                        disabled={contrato.yaAceptado}
                      />
                    }
                    label="He leído y acepto el contrato de uso de SizeSoftX para esta licencia."
                  />
                  <Divider />
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    disabled={!puedeAceptar}
                    onClick={aceptarContrato}
                    sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, px: 4 }}
                  >
                    {contrato.yaAceptado ? "Contrato aceptado" : "Aceptar contrato"}
                  </Button>
                </Stack>
              </Paper>
            </>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
};

export default ContratoPublicoPage;
